import { useEffect, useState } from "react";
import { Download, Pencil, Printer, Plus, Minus } from "lucide-react";
import Layout2 from "@/components/Layout/Layout2";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "react-hot-toast";
import {
  getEndOfDayReportSummary,
  printEndOfDayReportSummary,
  updateEndOfDayReportAdjustment,
} from "@/api/reporting";
import ReportingSummaryTable from "./components/reporting-summary-table";
import { useReportTills } from "./use-report-tills";
import {
  buildReportCsvName,
  buildSummaryRows,
  downloadCsv,
  formatCurrency,
  getTodayInputDate,
  parseTillId,
} from "./reporting-utils";
import hasPermission from "@/hooks/hasPermission";

function EndOfDayReportZSummary() {
  const [tillFilter, setTillFilter] = useState("all");
  const [showUnpaidModal, setShowUnpaidModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getTodayInputDate());
  const [summaryRows, setSummaryRows] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [unpaidValue, setUnpaidValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [adjustment, setAdjustment] = useState(0);
  const { tills } = useReportTills();

  const saveAdjustment = async (value) => {
    const tillId = parseTillId(tillFilter);
    const payload = {
      date: selectedDate,
      adjustment_value: value,
    };
    if (tillId !== null && tillId !== undefined) {
      payload.till_id = tillId;
    }

    const response = await updateEndOfDayReportAdjustment(payload);
    if (response?.status !== "success") {
      toast.error(response?.message || "Failed to update adjustment.");
    }
  };

  const handleIncrement = () => {
    setAdjustment((prev) => {
      const num = parseFloat(prev);
      const next = (isNaN(num) ? 0 : num) + 1;
      saveAdjustment(next);
      return next;
    });
  };

  const handleDecrement = () => {
    setAdjustment((prev) => {
      const num = parseFloat(prev);
      const next = (isNaN(num) ? 0 : num) - 1;
      saveAdjustment(next);
      return next;
    });
  };

  useEffect(() => {
    let isActive = true;

    const fetchReport = async () => {
      setIsLoading(true);
      const tillId = parseTillId(tillFilter);
      const response = await getEndOfDayReportSummary({
        date: selectedDate || undefined,
        till_id: tillId,
      });

      if (!isActive) return;

      if (response?.status === "success") {
        const data = response?.data || {};
        setReportData(data);
        const adjVal = data.adjustment_value ?? (typeof data.adjustment === "object" ? data.adjustment?.value : data.adjustment);
        setAdjustment(Number(adjVal ?? 0));
        setSummaryRows(
          buildSummaryRows({
            items: data.payment_methods,
            total: data.total,
            deductions: data.deductions,
            netTotal: data.sub_total,
            netLabel: "Sub Total",
          })
        );

        const unpaidEntry = (data.deductions || []).find(
          (item) =>
            item.key === "unpaid" ||
            String(item.label || "").toLowerCase() === "unpaid"
        );
        setUnpaidValue(Number(unpaidEntry?.total_value ?? 0));
      } else {
        toast.error(response?.message || "Failed to load summary report.");
        setSummaryRows([]);
        setReportData(null);
        setUnpaidValue(0);
        setAdjustment(0);
      }

      setIsLoading(false);
    };

    fetchReport();

    return () => {
      isActive = false;
    };
  }, [selectedDate, tillFilter]);

  useEffect(() => {
    setShowUnpaidModal(Math.abs(unpaidValue) > 0);
  }, [unpaidValue]);

  const handleExportCsv = () => {
    if (!summaryRows.length) {
      toast.error("No data available to export.");
      return;
    }

    const tillId = parseTillId(tillFilter);
    const fileName = buildReportCsvName(
      "end-of-day-z-summary",
      selectedDate,
      tillId
    );
    const subTotal = Number(reportData?.sub_total?.total_value || 0);
    const netTotal = subTotal + Number(adjustment || 0);
    const rows = summaryRows.map((row) => [row.label, row.count, row.value]);
    if (Number(adjustment || 0) !== 0) {
      rows.push(["Adjustment", "", formatCurrency(Number(adjustment || 0))]);
    }
    rows.push(["Net Total", "", formatCurrency(netTotal)]);

    downloadCsv(fileName, [
      "Payment Methods",
      "Number of Order/ Transactions",
      "Total Value",
    ], rows);
  };

  const handlePrint = async () => {
    setIsPrinting(true);
    const tillId = parseTillId(tillFilter);
    const response = await printEndOfDayReportSummary({
      date: selectedDate || undefined,
      till_id: tillId,
      adjustment: adjustment || undefined,
    });

    if (response?.status === "success") {
      toast.success(response?.message || "Summary print dispatched successfully.");
    } else {
      toast.error(response?.message || "Failed to print summary report.");
    }

    setIsPrinting(false);
  };

  return (
    <Layout2
      pageName={{ icon: true, title: "End of Day Report/Z (Summary)" }}
      paths={[
        { name: "Home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Reporting", path: "/reporting" },
        {
          name: "End of Day Report/Z (Summary)",
          path: "/reporting/end-of-day-z-summary",
        },
      ]}
    >
      <div className="px-4 pb-12">
        <div className="text-sm font-semibold text-cwhite">Search By Date</div>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <div className="w-max-content">
            <Input
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              className="bg-cblack3 text-sm"
            />
          </div>
          <Select value={tillFilter} onValueChange={setTillFilter}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Filter by Till" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Till</SelectItem>
              {tills.length === 0 ? (
                <SelectItem value="no-tills" disabled>
                  No tills available
                </SelectItem>
              ) : (
                tills.map((till) => (
                  <SelectItem key={till.id} value={String(till.id)}>
                    {till.device_name || `Till-${till.id}`}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          {hasPermission("reporting.reporting.print_end_of_day_report") && (
            <>
              <button
                type="button"
                onClick={handleExportCsv}
                className="flex h-9 items-center gap-2 rounded-md bg-cblack6 px-4 text-sm text-cwhite"
              >
                Export as CSV
                <Download size={16} />
              </button>
              <button
                type="button"
                onClick={handlePrint}
                disabled={isPrinting || isLoading}
                className="flex h-9 items-center gap-2 rounded-md bg-cblack6 px-4 text-sm text-cwhite"
              >
                {isPrinting ? "Printing..." : "Print"}
                <Printer size={16} />
              </button>
            </>
          )}
        </div>

        <div className="mt-4">
          {isLoading ? (
            <div className="rounded-md border border-cblack6 bg-cblack4 px-4 py-3 text-sm text-cwhite3">
              Loading report data...
            </div>
          ) : summaryRows.length > 0 ? (
            <ReportingSummaryTable rows={summaryRows} />
          ) : (
            <div className="rounded-md border border-cblack6 bg-cblack4 px-4 py-3 text-sm text-cwhite3">
              No data found for the selected filters.
            </div>
          )}
        </div>

        <div className="mt-6 rounded-md border border-cblack6 bg-cblack4 px-6 py-5">
          <div className="text-center text-sm font-semibold text-cwhite">
            Adjustments (Editable)
          </div>
          <div className="mt-4 flex justify-end">
            <div className="w-full max-w-md">
              <div className="flex items-center justify-between gap-6">
                <div className="flex items-center gap-2 text-sm text-cwhite3">
                  <Pencil size={14} className="text-cyellow" />
                  <span>Adjustment Value</span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleDecrement}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-cblack6 bg-cblack3 text-cwhite hover:bg-cblack5 hover:text-color8 active:scale-90 transition-all"
                    title="Decrement Adjustment"
                  >
                    <Minus size={16} />
                  </button>
                  <Input
                    type="number"
                    value={adjustment}
                    onChange={(e) => {
                      setAdjustment(e.target.value);
                    }}
                    onBlur={(e) => {
                      const val = parseFloat(e.target.value);
                      const finalVal = isNaN(val) ? 0 : val;
                      setAdjustment(finalVal);
                      saveAdjustment(finalVal);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const val = parseFloat(e.currentTarget.value);
                        const finalVal = isNaN(val) ? 0 : val;
                        setAdjustment(finalVal);
                        saveAdjustment(finalVal);
                        e.currentTarget.blur();
                      }
                    }}
                    className="h-9 w-28 bg-cblack3 text-center text-sm text-cwhite border-cblack6 focus-visible:ring-1 focus-visible:ring-cyellow focus-visible:border-cyellow"
                  />
                  <button
                    type="button"
                    onClick={handleIncrement}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-cblack6 bg-cblack3 text-cwhite hover:bg-cblack5 hover:text-color7 active:scale-90 transition-all"
                    title="Increment Adjustment"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between rounded-md border border-cblack6 bg-cblack4 px-6 py-4">
          <span className="text-sm font-semibold text-cyellow">Net Total</span>
          <span className="text-sm font-semibold text-cyellow">
            {formatCurrency(Number(reportData?.sub_total?.total_value || 0) + Number(adjustment || 0))}
          </span>
        </div>
      </div>

      {showUnpaidModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-md border border-cblack6 bg-cblack4 p-6 text-center">
            <h3 className="text-lg font-semibold text-color8">Are You Sure ?</h3>
            <p className="mt-2 text-sm text-cwhite3">
              Have you Complete your all Unpaid orders ?
            </p>
            <div className="mt-6 flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => setShowUnpaidModal(false)}
                className="rounded bg-color8 px-6 py-2 text-sm font-semibold text-cwhite"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setShowUnpaidModal(false)}
                className="rounded bg-color7 px-6 py-2 text-sm font-semibold text-cblack"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout2>
  );
}

export default EndOfDayReportZSummary;
